import { Button } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import CrossIcon from "../../assets/icons/cross-icon"
import DeleteIcon from "../../assets/icons/delete-icon"
import UploadIcon from "../../assets/icons/upload-icon"
import { finishWebView } from "../../shared/app-interface"
import { DOCUMENT_ASSESSMENT_STAGE, FILE_UPLOAD_SOURCE } from "../../shared/constants"
import { useAssessmentStore } from "../../shared/store"
import { getAssessmentTrackersData } from "../../shared/utils/get-assessment-trackers"
import BottomSheet from "../bottom-sheet"
import Shell from "../shell"
import Dialog from "../dialog"
import { trackMixpanelEvent } from "../../utils/helpers"
import { EVENTS } from "../../shared/constants/MIXAPANEL_EVENTS"
import FilePreviewIcon from "../../assets/icons/file-preview-icon"
import { httpLegacyClient } from "../../shared/http-client"
import { ApnaToast } from "../toast"
import { CompletionObjectType } from "../../shared/utils/assessment-types"
// import { Document, Page, pdfjs } from "react-pdf"
// pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

type ResumeAssessmentProps = {
    handleOnCompletion: (completionObject: CompletionObjectType) => void
    handleSkip: () => void
    webSuccessFlow: boolean
}

const ResumeAssessment = ({
    handleOnCompletion,
    handleSkip,
    webSuccessFlow,
}: ResumeAssessmentProps) => {
    const { assesmentQuestions, setOpenExitFlow, setOpenSuccessFlow, currentStep } =
        useAssessmentStore((state) => state)
    const isVasFlow = assesmentQuestions?.mandatory || false
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
    const [showExitPopup, setShowExitPopup] = useState(false)
    const [filePreviewObject, setFilePreviewObject] = useState({
        name: "",
        size: 0,
        type: "",
    })
    const [showRemovePopup, setShowRemovePopup] = useState(false)
    const [fileSelected, setFileSelected] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const fileBlobRef = useRef<Blob>()
    const entity = assesmentQuestions?.questions[currentStep]?.entity_type

    const [showProgressBar, setShowProgressbar] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [fileUploading, setFileUploading] = useState(false)
    const [renderPdf, setRenderPdf] = useState(false)
    const questionId = assesmentQuestions?.questions[currentStep]?.questions[0]?.id
    const questionType = assesmentQuestions?.questions[currentStep]?.question_type
    const handleUploadClick = () => {
        getAssessmentTrackersData(
            entity,
            DOCUMENT_ASSESSMENT_STAGE.UPLOAD_CTA_CLICK,
            isVasFlow
        )
        setIsBottomSheetOpen(true)
    }
    useEffect(() => {
        trackMixpanelEvent(EVENTS.ENRICHMENT_SCREEN_SHOWN, {
            Type: isVasFlow ? "VAS" : "Application flow enrichment",
            "Entity Shown": "Resume assesment",
        })
    }, [])
    const handleResumeUploadBottomSheetClose = () => {
        // if (reason === "backpress") {
        const isOnlyResumeUploadPopupOpen =
            useAssessmentStore.getState().popupStacks.length === 1
        const openSuccessFlow = useAssessmentStore.getState().openSuccessFlow
        const isFilePreview = filePreviewObject.size > 0

        if (isOnlyResumeUploadPopupOpen && isFilePreview && !openSuccessFlow) {
            setShowExitPopup(true)
        } else {
            setIsBottomSheetOpen(false)
        }
    }

    const handleRemoveClick = () => {
        setShowRemovePopup(true)
    }

    const handleRemoveResume = () => {
        getAssessmentTrackersData(
            entity,
            DOCUMENT_ASSESSMENT_STAGE.DOCUMENT_REMOVED_CLICK,
            isVasFlow
        )
        setFilePreviewObject({ name: "", size: 0, type: "" })
        setFileSelected(false)
        setShowRemovePopup(false)
        setRenderPdf(false)
    }
    const handleResumeSubmit = async () => {
        const completionObject = {
            question_id: questionId,
            question_type: questionType,
        }
        if (typeof fileBlobRef?.current === "object" && !!fileBlobRef?.current) {
            setShowProgressbar(true)
            setFileUploading(true)
        } else {
            ApnaToast.show(
                <div className="flex items-center">Please select a valid file</div>,
                { className: "mb-32" }
            )
        }

        getAssessmentTrackersData(
            entity,
            DOCUMENT_ASSESSMENT_STAGE.DOCUMENT_UPLOAD_INITIATED,
            isVasFlow
        )

        const uploadURL =
            assesmentQuestions?.questions?.[currentStep]?.questions?.[0]?.upload_data
                ?.upload_urls?.[0]
        const uploadSignedHeaders =
            assesmentQuestions?.questions?.[currentStep]?.questions?.[0]?.upload_data
                ?.signed_headers

        try {
            await httpLegacyClient
                .put(uploadURL, fileBlobRef?.current, {
                    headers: {
                        ...uploadSignedHeaders,
                        "Content-Type": filePreviewObject?.type,
                    },
                    onUploadProgress: (e) => {
                        setUploadProgress(Math.floor((e?.progress || 0) * 100))
                    },
                })
                .then((resp) => resp.data)

            getAssessmentTrackersData(
                entity,
                DOCUMENT_ASSESSMENT_STAGE.DOCUMENT_UPLOAD_SUCCESS,
                isVasFlow
            )
            setFilePreviewObject({ name: "", size: 0, type: "" })
            if (isVasFlow) {
                trackMixpanelEvent(EVENTS.ASSESSMENT_PARTIAL_EVALUATION, {
                    "Section Name": "VAS Resume",
                    "Section Result": true,
                })
            }
            setFileUploading(false)
            setShowProgressbar(false)
            setUploadProgress(0)
            setIsBottomSheetOpen(false)
            handleOnCompletion(completionObject)
        } catch (e) {
            console.log(e)
            getAssessmentTrackersData(
                entity,
                DOCUMENT_ASSESSMENT_STAGE.DOCUMENT_UPLOAD_FAILURE,
                isVasFlow
            )
            ApnaToast.show(
                <div className="flex items-center">
                    There was an error uploading resume. Please try again
                </div>,
                { className: "mb-32" }
            )
            setUploadProgress(0)
            setFileUploading(false)
            setShowProgressbar(false)
        }
    }

    const handleExitWebview = () => {
        const isVasFlow = useAssessmentStore.getState().assesmentQuestions.mandatory
        if (isVasFlow) {
            finishWebView(
                JSON.stringify({
                    intent: "exit",
                    flowType: "vas",
                })
            )
        } else {
            // closing all and opening success flow in case of enrichment flow
            // finishWebflow call here
            if (!webSuccessFlow) {
                const data = {
                    intent: "navigate",
                    flowType: isVasFlow ? "vas" : "enrichment",
                    navigateTo: "screen_details",
                }
                finishWebView(JSON.stringify(data))
            } else {
                setIsBottomSheetOpen(false)
                setOpenExitFlow(false)
                setShowExitPopup(false)
                setOpenSuccessFlow(true)
            }
        }
    }

    const handleFileChange = (e: any) => {
        getAssessmentTrackersData(
            entity,
            DOCUMENT_ASSESSMENT_STAGE.ADD_DOCUMENT_CLICK,
            isVasFlow,
            FILE_UPLOAD_SOURCE.FILES
        )

        const fileObj = e.target.files && e.target.files[0]
        if (!fileObj) {
            e.target.value = null
            return
        } else {
            const fileType = fileObj.type
            const sizeLimit = 5 * 1000 * 1000
            const mimeTypes = [
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "application/pdf",
                "image/jpeg",
            ]
            if (!mimeTypes.includes(fileType)) {
                ApnaToast.show(
                    <div className="flex items-center">
                        Please upload .pdf, .docx or .jpeg format only
                    </div>,
                    { className: "mb-32" }
                )
                e.target.value = null
                return
            }
            if (fileObj.size > sizeLimit) {
                ApnaToast.show(
                    <div className="flex items-center">
                        File size should not exceed 5 MB.
                    </div>,
                    { className: "mb-32" }
                )
                e.target.value = null
                return
            }
            fileBlobRef.current = fileObj
            setFileSelected(true)
            setFilePreviewObject({
                type: fileType,
                name: fileObj?.name,
                size: fileObj?.size,
            })
            console.log("file attributes", fileObj)
            if (
                fileBlobRef?.current?.type === ".pdf" ||
                fileBlobRef?.current?.type === "application/pdf"
            ) {
                // set True if pdf Preview Required otherwise fall back to default behaviour
                // setRenderPdf(true)
            }
        }
    }
    return (
        <Shell
            footerElement={
                <div className="flex flex-col gap-4 items-center justify-center w-full">
                    {isVasFlow && (
                        <span className="text-onSurface-secondary text-xs text-center px-[35px] ">
                            Your Application would not be Successful if you do not
                            complete this step
                        </span>
                    )}

                    <div className="flex w-full flex-col items-center justify-center px-6 mb-10">
                        <Button
                            fullWidth
                            onClick={handleUploadClick}
                            style={{ padding: "16px", borderRadius: "4px" }}
                        >
                            <span>Upload</span>
                        </Button>
                        {!isVasFlow && (
                            <Button onClick={handleSkip} variant="text">
                                Skip
                            </Button>
                        )}
                    </div>
                </div>
            }
        >
            <>
                <BottomSheet
                    sheetId="file-upload"
                    PaperProps={{
                        sx: {
                            borderRadius: "16px 16px 0 0",
                        },
                    }}
                    open={isBottomSheetOpen}
                    anchor="bottom"
                    onClose={handleResumeUploadBottomSheetClose}
                >
                    <div className="rounded-t-lg">
                        <div className="flex justify-between py-6 px-4 border-b">
                            <div>
                                <h3 className="text-base font-semibold text-onSurface-primary">
                                    Upload your Resume
                                </h3>
                                <p className="text-onSurface-secondary text-sm">
                                    Only visible to HR
                                </p>
                            </div>
                            <button onClick={handleResumeUploadBottomSheetClose}>
                                <CrossIcon />
                            </button>
                        </div>
                        <div className="p-4">
                            {!fileSelected && (
                                <>
                                    <div
                                        onClick={() => fileInputRef.current?.click()}
                                        className="h-[353px] flex flex-col items-center justify-center bg-opacity-40 rounded bg-onSurface-disabled border border-solid border-onSurface-secondary w-full"
                                    >
                                        {/* Upload Icon */}
                                        <UploadIcon />
                                        <input
                                            onChange={(e) => handleFileChange(e)}
                                            ref={fileInputRef}
                                            className="hidden"
                                            type="file"
                                            // accept=".docx, .doc, .pdf, .jpeg"
                                        />
                                        {/* Text Container */}
                                        <div className="text-center mt-2">
                                            <p className="font-semibold text-sm">
                                                Click here to upload
                                            </p>
                                            <p className="text-onSurface-secondary text-xs">
                                                Upload .pdf, .jpeg or .docx file only
                                            </p>
                                            <p className="text-onSurface-secondary text-xs">
                                                Max file size: 5 MB
                                            </p>
                                        </div>
                                    </div>
                                    <div className="w-full mt-5">
                                        <p className="font-semibold text-xs">
                                            Tips for a good resume
                                        </p>
                                        <ul className="mt-2 ml-4 text-xs list-disc text-onSurface-secondary mb-6">
                                            <li>
                                                Your name and mobile number should be
                                                mentioned in the resume
                                            </li>
                                            <li>
                                                Highlight important details about your
                                                work and education
                                            </li>
                                            <li>
                                                Try to keep your resume to 1-2 pages only
                                            </li>
                                        </ul>
                                    </div>
                                </>
                            )}
                            {fileSelected && (
                                <>
                                    <div
                                        className={`relative ${
                                            renderPdf
                                                ? "bg-onSurface-primary"
                                                : "bg-onSurface-disabled"
                                        } flex flex-col items-center justify-center h-[460px] w-full overflow-y-hidden px-3 pt-4 rounded-md`}
                                    >
                                        {/* {renderPdf && (
                                            <>
                                                <Document
                                                    file={fileBlobRef.current}
                                                    onLoadError={(e) => console.log(e)}
                                                    className="flex justify-center items-center max-w-full overflow-hidden"
                                                >
                                                    <Page
                                                        pageNumber={1}
                                                        height={460}
                                                        renderTextLayer={false}
                                                        renderAnnotationLayer={false}
                                                    />
                                                </Document>
                                                <p className="absolute bottom-0 left-0 bg-[#4D3951B2] w-full text-white font-medium text-xs px-4 py-2">
                                                    {filePreviewObject?.name}
                                                </p>
                                            </>
                                        )} */}

                                        {!renderPdf && (
                                            <>
                                                <FilePreviewIcon />
                                                <p className="mt-4 text-sm text-onSurface-primary">
                                                    {filePreviewObject?.name}
                                                </p>

                                                {filePreviewObject?.size && (
                                                    <p className="mt-1 text-sm text-onSurface-secondary">
                                                        {(
                                                            filePreviewObject?.size /
                                                            1048576
                                                        ).toFixed(2)}{" "}
                                                        MB
                                                    </p>
                                                )}
                                                <div className="flex items-center text-xs absolute bottom-0 h-[20px] w-full bg-[#4D3951B2]">
                                                    <p className="ml-2 text-[#F5F5F5] font-medium">
                                                        {filePreviewObject?.name}
                                                    </p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {showProgressBar && (
                                        <div className="w-full mt-4 mb-10">
                                            <div className="rounded bg-[#E5F8F1]">
                                                <div
                                                    className="h-3 rounded bg-[#1C9273] transition-all duration-300"
                                                    style={{
                                                        width: `${uploadProgress}%`,
                                                    }}
                                                ></div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex justify-between mt-4">
                                        <Button
                                            onClick={handleRemoveClick}
                                            startIcon={<DeleteIcon />}
                                            color="error"
                                            variant="outlined"
                                            disabled={fileUploading}
                                        >
                                            Remove
                                        </Button>
                                        <Button
                                            onClick={handleResumeSubmit}
                                            disabled={fileUploading}
                                        >
                                            <div className="flex gap-1">
                                                {/* {isUploaded && (
                                                    <CircularProgress
                                                        size={19}
                                                        style={{ color: "white" }}
                                                    />
                                                )} */}
                                                Submit
                                            </div>
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </BottomSheet>
                <Dialog
                    onClose={() => null}
                    dialogId="remove-file"
                    open={showRemovePopup}
                    sx={{
                        "& .MuiPaper-root": {
                            width: "80%",
                        },
                    }}
                >
                    <div className="mx-auto px-4 py-10">
                        <h3
                            className="text-center text-onSurface-primary font-semibold
                    "
                        >
                            Are you sure you want to remove this Resume?
                        </h3>
                        <div className="flex space-between mt-8 space-x-4">
                            <Button
                                onClick={() => setShowRemovePopup(false)}
                                className="w-full"
                                variant="outlined"
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleRemoveResume} className="w-full">
                                Yes
                            </Button>
                        </div>
                    </div>
                </Dialog>
                <Dialog
                    onClose={() => null}
                    dialogId="exit-popup"
                    open={showExitPopup}
                    sx={{
                        "& .MuiPaper-root": {
                            width: "80%",
                        },
                    }}
                >
                    <div className="mx-auto px-4 py-10">
                        <h3
                            className="text-center text-onSurface-primary font-semibold
                    "
                        >
                            Do you want to exit? Your changes will not be saved.
                        </h3>
                        <div className="flex space-between mt-8 space-x-4">
                            <Button
                                onClick={() => setShowExitPopup(false)}
                                className="w-full"
                            >
                                No
                            </Button>
                            <Button
                                onClick={handleExitWebview}
                                className="w-full"
                                variant="outlined"
                            >
                                Yes
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </>
        </Shell>
    )
}
export default ResumeAssessment
